import { supabase } from "@/lib/supabase";
import { AuthUser } from "@/types";

export async function logUserSession({
  user,
  action,
  details = "",
  ipAddress = "",
  deviceInfo = "",
}: {
  user: AuthUser;
  action: "login" | "logout" | "view" | "edit" | "delete";
  details?: string;
  ipAddress?: string;
  deviceInfo?: string;
}) {
  if (!user) {
    console.warn('No user provided to logUserSession');
    return;
  }

  // Skip logging for admin users
  if (user.role === 'admin') {
    return;
  }

  // Validating user session data

  // Ensure we have required fields
  const userName = user.name || user.email || 'Unknown User';
  const userRole = user.role || 'unknown';

  // Best-effort network info capture when not provided
  async function getClientIp(): Promise<string> {
    // Prefer IPv4 endpoints; fallback to mixed/IPv6
    const endpoints = [
      // IPv4-only plain text endpoints
      { url: 'https://api.ipify.org?format=text', parse: async (r: Response) => (await r.text()).trim() },
      { url: 'https://api4.ipify.org?format=text', parse: async (r: Response) => (await r.text()).trim() },
      { url: 'https://ipv4.icanhazip.com', parse: async (r: Response) => (await r.text()).trim() },
      { url: 'https://v4.ident.me', parse: async (r: Response) => (await r.text()).trim() },
      { url: 'https://ifconfig.me/ip', parse: async (r: Response) => (await r.text()).trim() },
      // JSON as fallback (may return v6)
      { url: 'https://api.ipify.org?format=json', parse: async (r: Response) => (await r.json())?.ip as string },
      { url: 'https://api64.ipify.org?format=json', parse: async (r: Response) => (await r.json())?.ip as string },
    ];
    for (const ep of endpoints) {
      try {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 3000);
        const resp = await fetch(ep.url, { signal: controller.signal, cache: 'no-store', mode: 'cors' });
        clearTimeout(timeout);
        if (!resp.ok) continue;
        const ip = await ep.parse(resp);
        if (typeof ip === 'string' && ip.length > 0) {
          // Prefer IPv4; if the result looks like IPv6 (contains ':'), try next endpoint
          if (ip.includes(':')) continue;
          return ip;
        }
      } catch {
        // try next endpoint
      }
    }
    return '';
  }

  const resolvedDeviceInfo = deviceInfo || (typeof navigator !== 'undefined' ? navigator.userAgent : '');
  
  // Try to get IP address first if not provided
  let resolvedIpAddress = ipAddress;
  if (!ipAddress) {
    try {
      resolvedIpAddress = await getClientIp();
    } catch (error) {
      console.warn('Failed to resolve IP address synchronously:', error);
      resolvedIpAddress = '';
    }
  }
  
  // Insert log with resolved IP address
  try {
    const timestamp = new Date().toISOString();
    const { error } = await supabase.from("user_session_logs").insert([
      {
        user_id: user.id,
        user_name: userName,
        user_role: userRole,
        action,
        details,
        ip_address: resolvedIpAddress,
        device_info: resolvedDeviceInfo,
        timestamp: timestamp,
      },
    ]);

    if (error) {
      console.error('Error logging user session:', error);
    }
  } catch (error) {
    console.error('Error in logUserSession:', error);
  }
} 